import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Tabs, Table, InputNumber, Space, Popconfirm, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@components/shared/PageHeader';
import { shippingRateCardsService } from '@services/operator/shippingRateCardsService';

const { Text } = Typography;

const TiersTable = ({ form, name, label, unitLabel }) => {
  const { t } = useTranslation();

  // 監聽 form values 變化以即時計算範圍
  const tiers = Form.useWatch(name, form) || [];

  // 自動排序並計算區間
  // 注意：這裡只做顯示優化，不改變 form 內的順序以免影響編輯體驗，但最好的做法是讓使用者輸入完後自動排序
  // 為了簡單起見，我們假設使用者會依序輸入，或者我們可以在渲染時顯示「上一級距」的提示

  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => {
        // 為了計算區間，我們需要知道每個 field 對應的 current value
        // 這裡我們建立一個 helper map
        const sortedFields = [...fields].sort((a, b) => {
          const valA = form.getFieldValue([name, a.name, 'max_value']) || 0;
          const valB = form.getFieldValue([name, b.name, 'max_value']) || 0;
          return valA - valB;
        });

        return (
          <div>
            <Table
              dataSource={fields} // 使用原始 fields 以保持輸入焦點，但可以考慮顯示排序後的視圖
              // 為了不打斷輸入體驗，我們暫時不強制排序，但在顯示時計算 "From"
              rowKey="key"
              pagination={false}
              footer={() => (
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {t('operator.shipping_rate_cards.form.add_tier')}
                </Button>
              )}
              columns={[
                {
                  title: t('operator.shipping_rate_cards.form.tier_range', { defaultValue: 'Range' }),
                  key: 'range',
                  width: 250,
                  render: (_, field, index) => {
                    // 找出當前 field 的 max_value
                    const currentMax = form.getFieldValue([name, field.name, 'max_value']);
                    
                    // 找出「比當前 max_value 小」的最大值作為 min_value
                    // 1. 取得所有 tiers 的 max_value
                    const allValues = fields.map(f => ({
                      key: f.key,
                      val: form.getFieldValue([name, f.name, 'max_value']) || 0
                    }));
                    
                    // 2. 過濾並排序
                    const prevValues = allValues
                      .filter(v => v.val < (currentMax || Infinity) && v.key !== field.key)
                      .sort((a, b) => b.val - a.val); // 降序

                    const minVal = prevValues.length > 0 ? prevValues[0].val : 0;

                    return (
                      <Space>
                        <Text type="secondary">{minVal} {unitLabel}</Text>
                        <ArrowRightOutlined style={{ color: '#bfbfbf', fontSize: '12px' }} />
                        <Form.Item
                          {...field}
                          name={[field.name, 'max_value']}
                          rules={[{ required: true, message: t('common.validation.required') }]}
                          noStyle
                        >
                          <InputNumber 
                            min={0} 
                            precision={3} 
                            style={{ width: 120 }} 
                            placeholder={t('models.shipping_tier.max_value')}
                          />
                        </Form.Item>
                        <Text>{unitLabel}</Text>
                      </Space>
                    );
                  },
                },
                {
                  title: t('models.shipping_tier.price'),
                  dataIndex: 'price',
                  key: 'price',
                  width: 200,
                  render: (_, field) => (
                    <Form.Item
                      {...field}
                      name={[field.name, 'price']}
                      rules={[{ required: true, message: t('common.validation.required') }]}
                      noStyle
                    >
                      <InputNumber 
                        min={0} 
                        precision={2} 
                        style={{ width: '100%' }} 
                        prefix="$"
                      />
                    </Form.Item>
                  ),
                },
                {
                  key: 'action',
                  width: 50,
                  render: (_, field) => (
                    <>
                      <Form.Item
                        {...field}
                        name={[field.name, 'id']}
                        hidden
                      >
                        <Input />
                      </Form.Item>
                      <Popconfirm
                        title={t('operator.shipping_rate_cards.form.confirm_delete')}
                        onConfirm={() => remove(field.name)}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    </>
                  ),
                },
              ]}
            />
            <div style={{ marginTop: 8, color: '#888', fontSize: '12px' }}>
              * {t('operator.shipping_rate_cards.form.tier_hint', { defaultValue: 'Tiers are automatically sorted by max value. The system will match the smallest tier that covers the shipment weight/volume.' })}
            </div>
          </div>
        );
      }}
    </Form.List>
  );
};

const ShippingRateCardsEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const isNew = !isEdit;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [originalTiers, setOriginalTiers] = useState([]);

  useEffect(() => {
    if (isEdit) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await shippingRateCardsService.fetchOne(id);
      if (response.success) {
        const data = response.data;
        const tiers = (data.shipping_tiers || []).map(t => ({
          ...t,
          max_value: parseFloat(t.max_value),
          price: parseFloat(t.price)
        }));
        
        // Sort by max_value for better UX
        const weightTiers = tiers
          .filter(t => t.condition_type === 'weight')
          .sort((a, b) => a.max_value - b.max_value);
        const volumeTiers = tiers
          .filter(t => t.condition_type === 'volume')
          .sort((a, b) => a.max_value - b.max_value);
        
        setOriginalTiers(tiers);
        
        form.setFieldsValue({
          name: data.name,
          weight_tiers: weightTiers,
          volume_tiers: volumeTiers
        });
      }
    } catch (error) {
      console.error('[ShippingRateCards.Edit] loadData failed:', error);
      message.error(t('common.messages.error.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  // 組合送出 payload（兩個提交按鈕共用）
  const buildPayload = (values) => {
    const formWeightTiers = values.weight_tiers || [];
    const formVolumeTiers = values.volume_tiers || [];

    const attributes = [];
    formWeightTiers.forEach(tier => attributes.push({ ...tier, condition_type: 'weight' }));
    formVolumeTiers.forEach(tier => attributes.push({ ...tier, condition_type: 'volume' }));

    const payload = {
      name: values.name,
      shipping_tiers_attributes: attributes,
    };

    if (isEdit && originalTiers.length > 0) {
      const currentIds = new Set(attributes.map(a => a.id).filter(Boolean));
      const deletedTiers = originalTiers.filter(tier => !currentIds.has(tier.id));
      deletedTiers.forEach(tier => {
        payload.shipping_tiers_attributes.push({ id: tier.id, _destroy: true });
      });
    }

    return payload;
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  const submitValues = async (values) => {
    const payload = buildPayload(values);
    return isEdit
      ? await shippingRateCardsService.update(id, payload)
      : await shippingRateCardsService.create(payload);
  };

  // 主要提交：成功後導向 edit 頁（此資源沒有獨立 show 路由，§4d）
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await submitValues(values);

      if (response.success) {
        message.success(t('operator.shipping_rate_cards.form.save_success'));
        // 此資源沒有獨立 show 路由，導向 edit 頁
        const recordId = response.data?.id || id;
        navigate(`/shipping_rate_cards/${recordId}/edit`, { replace: true });
      } else {
        message.error(response.message || t('operator.shipping_rate_cards.form.save_error'));
      }
    } catch (error) {
      console.error('[ShippingRateCards.Edit] handleSubmit failed:', error);
      message.error(t('operator.shipping_rate_cards.form.save_error'));
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);

        if (response.success) {
          message.success(t('operator.shipping_rate_cards.form.save_success'));
          // 重置 antd form（會清掉 weight_tiers / volume_tiers Form.List）+ originalTiers useState
          form.resetFields();
          setOriginalTiers([]);
        } else {
          message.error(response.message || t('operator.shipping_rate_cards.form.save_error'));
        }
      } catch (error) {
        console.error('[ShippingRateCards.Edit] handleSaveAndAddAnother failed:', error);
        message.error(t('operator.shipping_rate_cards.form.save_error'));
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        items={[
          'shipping_settings',
          { label: t('operator.menu.shipping_settings.shipping_rate_cards'), path: '/shipping_rate_cards' },
          { label: isEdit ? t('operator.shipping_rate_cards.form.title_edit') : t('operator.shipping_rate_cards.form.title_create') }
        ]}
      />
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item 
            name="name" 
            label={t('models.shipping_rate_card.name')} 
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Input />
          </Form.Item>

          <Tabs defaultActiveKey="weight" items={[
            {
              key: 'weight',
              label: t('operator.shipping_rate_cards.form.weight_tiers'),
              children: <TiersTable form={form} name="weight_tiers" unitLabel="kg" />
            },
            {
              key: 'volume',
              label: t('operator.shipping_rate_cards.form.volume_tiers'),
              children: <TiersTable form={form} name="volume_tiers" unitLabel="cm³" />
            }
          ]} />

          <Form.Item style={{ marginTop: 24 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                disabled={savingAndAdding}
              >
                {isNew ? t('common.button.create') : t('common.button.save')}
              </Button>
              {isNew && (
                <Button
                  loading={savingAndAdding}
                  disabled={loading}
                  onClick={handleSaveAndAddAnother}
                >
                  {t('common.button.save_and_add_another')}
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
    </div>
  );
};

export default ShippingRateCardsEdit;
